<template lang="pug">
//- Minimal page without AppLayout/nav
.div.sunset-page
  v-alert(:value="true" color="error" dense class="top-banner")
    | LionDesk has been sunset. Please select an option below to continue:
  v-container(fluid)
    //- 1) Migration in progress state
    template(v-if="isMigrationInProgress")
      .center-wrap
        v-card.elevation-1.peach-card
          v-card-text.text-xs-center
            .subheading.font-weight-bold.mb-2 We’re migrating your data to Relationships.
            .body-1.mb-4
              | Click 
              strong Go to Relationships
              |  to explore its features while your data migration is in progress.
            .mb-4
              v-chip(color="orange darken-2" dark) MIGRATION STATUS: IN PROGRESS
            .text-xs-center
              v-btn.cta-btn(color="primary" large @click="goToRelationships") Go to Relationships

    //- 2) Migration complete state
    template(v-else-if="isMigrationComplete && user.lwa_id && hasRelationshipsLicense")
      .center-wrap
        v-card.elevation-1.mint-card
          v-card-text.text-xs-center
            .subheading.font-weight-bold.mb-2 We’ve already migrated your data.
            .body-1.mb-4
              | Click 
              strong Go to Relationships
              |  now to view your migrated data in Relationships!
            .mb-4
              v-chip(color="teal darken-2" dark) MIGRATION STATUS: COMPLETED
            .text-xs-center
              v-btn.cta-btn(color="primary" large @click="goToRelationships") Go to Relationships

    //- 3) Default state (no migration in progress and not complete yet)
    template(v-else)
      .center-wrap
        v-card.elevation-1.teal-card
          v-card-text
            .subheading.text-xs-center.font-weight-medium.mb-3 LionDesk is no longer active. But great news—you can continue building smarter client connections with Relationships.
            .subheading.text-xs-center.font-weight-bold.mb-1 Switch to Relationships
            .body-1.text-xs-center.mb-4 Easily migrate your LionDesk contacts and data to Relationships in just a few steps.
            .text-xs-center
              v-btn.cta-btn(v-if="showSwitchToRelationships" color="primary" large :disabled="isPollingLicenses || switchToRelationshipsClicked" @click="handleSwitchToRelationships") Make the Switch to Relationships
        v-card.elevation-1.grey-card
          v-card-text.text-xs-center
            .subheading.font-weight-bold.mb-2 Not interested in making the switch to Relationships?
            .body-1
              | Contact us at 
              a(:href="mailtoHref") {{ supportEmail }}
              |  to cancel.

    template(v-if="isAuthedUser")
      .text-xs-center.mt-4
        v-btn(color="primary" outline large @click="goToDashboard") Admin


    template()
      .text-xs-center.mt-4
        v-btn(color="primary" id="lwt-unified-navbar__user-menu-toggle" outline large @click="logout") Logout

    v-dialog(:value="isPollingLicenses" persistent max-width="400px")
      v-card.elevation-2.loader-card
        v-card-text.text-xs-center
          v-progress-circular(indeterminate color="primary" :size="56" :width="5" class="mb-4")
          .subheading.font-weight-bold.mb-1 We’re setting up your account…
          .body-1 This usually takes less than a minute. We’ll take you to Relationships as soon as it’s ready.

</template>

<script>
import { createNamespacedHelpers, mapActions } from 'vuex';

const { mapState: mapLdAppLayoutState, mapMutations: mapLdAppLayoutMutations, mapActions: mapLdAppLayoutActions } =
  createNamespacedHelpers('ldAppLayout');

const nonSunsetMLS = ['16BcmCS3cUX8E1gG0','16BZALS3cTj2o1iHu','LIONDESK1327602PLANABC123','1ps2gs9RV5mK7htPP','2smoc9EuRF9ud1K1Hxi','AzZMr8Syri06W110k'];
const sunsetMLS = ['HngTojeR1aC0f2IuR', 'AzyWC9RwRzp7C23sH', 'AzZcjYS2r29CE1ngw', 'LIONDESK1327605PLANABC123'];
const carMLS = ['16BcmCS3cUX8E1gG0','16BZALS3cTj2o1iHu', '1ps2gs9RV5mK7htPP','2smoc9EuRF9ud1K1Hxi','AzZMr8Syri06W110k'];

const {
  mapState: mapUserInformationState,
  mapActions: mapUserInformationActions,
  mapGetters: mapUserInformationGetters,
} = createNamespacedHelpers('userInformation');

export default {
  name: 'Sunset',
  data() {
    return {
      isMigratingContacts: false,
      isPollingLicenses: false,
      showSwitchToRelationships: false,
      switchToRelationshipsClicked: false,
    };
  },
  computed: {
    ...mapLdAppLayoutState([
      'ulClientLicenses',
      'isMigrationComplete',
      'isMigrationInProgress',
      'createRelationshipsLicenseSuccess',
    ]),
    ...mapUserInformationState(['user']),
    relationshipsUrl() {
      return process.env.VUE_APP_RELATIONSHIPS_URL || 'https://crm.lwolf.com';
    },
    supportEmail() {
      return 'support@lwolf.com';
    },
    mailtoHref() {
      return `mailto:${this.supportEmail}`;
    },
    hasRelationshipsLicense() {
      return this.ulClientLicenses && this.ulClientLicenses.includes('Relationships');
    },
    nonSunsetMLSUser() {
      return this.user 
        && this.user.subscriptionOverview 
        && nonSunsetMLS.includes(this.user.subscriptionOverview.id_chargebee.String);
    },
    sunsetMLSUser() {
      return this.user 
        && this.user.subscriptionOverview 
        && sunsetMLS.includes(this.user.subscriptionOverview.id_chargebee.String);
    },
    isCARMLS() {
      return this.user 
        && this.user.subscriptionOverview 
        && carMLS.includes(this.user.subscriptionOverview.id_chargebee.String);
    },
    isAuthedUser() {
      const authorizedUsers = (process.env.VUE_APP_AUTHORIZED_USERS || '').split(',').map(id => parseInt(id, 10));
      console.log('Authorized Users:', authorizedUsers);
      if (!this.user) {
        console.log('No user is logged in.');
        return false;
      }
      return this.user && authorizedUsers.includes(this.user.id);
    },
  },
  methods: {
    ...mapLdAppLayoutActions([
      'getUserULClients',
      'checkMigrationStatus',
      'checkMigrationInProgress',
      'createRelationshipLicense',
      'createSunsetRelationshipsTrackingEvent',
    ]),
    ...mapActions( 'userInformation', [
      'getUser'
    ]),
    logout() {
      const ref = document.location.href;
      window.$cookies.set('LD_REDIRECT_URL', ref, null, null, process.env.VUE_APP_MAIN_DOMAIN);
      window.$cookies.remove('LD_AUTH', null, process.env.VUE_APP_MAIN_DOMAIN);
      window.$cookies.remove('feathers-jwt', null, process.env.VUE_APP_MAIN_DOMAIN);
      window.$cookies.remove('RL6Token', null, process.env.VUE_APP_MAIN_DOMAIN);
      window.lwcrm = undefined;
      window.location = `${process.env.VUE_APP_ACCOUNTS_APP_URL}/signin`;
    },
    sleep(ms) {
      return new Promise((resolve) => setTimeout(resolve, ms));
    },
    async pollForRelationshipsLicense({ intervalMs = 3000, timeoutMs = 180000, postDelayMs = 4000 } = {}) {
      const start = Date.now();
      while (Date.now() - start < timeoutMs) {
        try {
          await this.getUserULClients();
        } catch (e) {
          // Non-fatal: wait and retry
        }

        if (this.hasRelationshipsLicense) {
          // Give the downstream app a moment to finish provisioning
          await this.sleep(postDelayMs);
          return true;
        }

        await this.sleep(intervalMs);
      }
      return false;
    },
    async waitForRelationshipsLicenseAndRedirect() {
      if (this.isPollingLicenses) return;
      this.isPollingLicenses = true;
      const found = await this.pollForRelationshipsLicense();
      this.isPollingLicenses = false;
      if (found) {
        this.goToRelationships();
      }
    },
    maybeRedirectUser() {
      if (this.$cookies.get('sunset_bypass') === '1') return;
      const authorizedUsers = (process.env.VUE_APP_AUTHORIZED_USERS || '').split(',').map(id => parseInt(id, 10));
      if (!this.user || authorizedUsers.includes(this.user.id)) return;

      if (['cancelled', 'trial', 'trial_expired'].includes(this.user.subscriptionOverview.txt_status)) {
        window.location.href = 'https://www.lwolf.com/engage/relationships?utm_source=liondesk&utm_medium=redirect&utm_campaign=eol_upgrade';
        return;
      }

      const u = this.user || {};
      if (
        u.id &&
        !this.$cookies.get(`UL_OFF_${u.id}`) &&
        !this.nonSunsetMLSUser &&
        ((u.saw_billing_migration === 'true' || u.saw_billing_migration === true) || this.sunsetMLSUser)
      ) {
        if (Array.isArray(this.ulClientLicenses) && this.ulClientLicenses.includes('Relationships')) {
          window.location.href = this.relationshipsUrl;
          return;
        } else if (Array.isArray(this.ulClientLicenses)) {
          window.location.href = 'https://www.lwolf.com/engage/relationships?utm_source=liondesk&utm_medium=redirect&utm_campaign=eol_upgrade';
          return;
        }
        // Licenses not loaded yet; ulClientLicenses watcher will re-check.
      }

      // CAR MLS redirect
      if (u.id && this.isCARMLS) {
        window.location.href = 'https://lonewolf.my.site.com/s/article/LD-C-A-R-data-migration-from-LionDesk-to-Relationships';
      }
    },
    goToDashboard() {
      this.$router.push({ name: 'dashboard' });
    },
    goToRelationships() {
      window.location.href = this.relationshipsUrl;
    },
    async handleSwitchToRelationships() {
      this.switchToRelationshipsClicked = true;
      this.createSunsetRelationshipsTrackingEvent();
      if (!this.user.lwa_id) {
        let url = `${process.env.VUE_APP_UL_PLATFORM_URL}/authenticate?r=${process.env.VUE_APP_ACCOUNTS_APP_URL}/unified-login?&e=${this.user.email}`;
        window.location.href = url;
        return;
      }

      if (this.ulClientLicenses.includes('Relationships')) {
        window.location.href = this.relationshipsUrl;
        return;
      }
      
      await this.createRelationshipLicense();
      // After requesting license creation, poll until license is visible, then redirect
      this.waitForRelationshipsLicenseAndRedirect();
    }
  },
  async created() {
    await this.getUser();
    // First, load licenses in parallel context
    await this.getUserULClients();

    // Check migration state in the required order:
    // 1) If in progress, we render the special view and stop.
    await this.checkMigrationInProgress();
    if (!this.isMigrationInProgress) {
      // 2) Only if not in progress, check if it's complete.
      await this.checkMigrationStatus();
    }
    // After initial data loads, run redirect logic once
    this.showSwitchToRelationships = true;
    this.maybeRedirectUser();
  },
  watch: {
    createRelationshipsLicenseSuccess(isSuccessful) {
      if (isSuccessful) {
        // When the API reports license creation success, begin polling until
        // the Relationships license appears, then redirect.
        this.waitForRelationshipsLicenseAndRedirect();
      }
    },
    user() {
      this.maybeRedirectUser();
    },
    ulClientLicenses() {
      this.maybeRedirectUser();
    },
  },
};
</script>

<style scoped lang="scss">
.sunset-page {
  min-height: 100vh;
  background: #fff;
}

.top-banner {
  border-radius: 0 !important;
  text-align: center;
  margin: 0;
  background-color: #f8d7da !important;
  color: #6b1a1e !important;
  border-bottom: 1px solid #eab5b8;
  padding-top: 20px !important;
  padding-bottom: 20px !important;
  font-size: 16px;
  font-weight: 700;
}

.center-wrap {
  max-width: 920px;
  margin: 40px auto;
}

.center-wrap>.v-card {
  margin-top: 36px;
}

.center-wrap>.v-card:first-child {
  margin-top: 0;
}

.teal-card {
  background: #d6f3f0;
}

.grey-card {
  background: #f5f7f9;
}

.peach-card {
  background: #fbe8db;
  /* soft peach to match mock */
  border: 1px solid #f2a46b;
}

.mint-card {
  background: #d9f2ee;
  /* soft mint to match "completed" mock */
  border: 1px solid #33a39a;
}


/deep/ .teal-card .v-card__text,
/deep/ .grey-card .v-card__text {
  padding: 36px 32px;
}

/deep/ .headline {
  font-size: 3rem;
  line-height: 1.3;
}

/deep/ .subheading {
  font-size: 1.5rem;
}

/deep/ .body-1 {
  font-size: 1.125rem;
  line-height: 1.8;
}

/deep/ .cta-btn.v-btn {
  height: 56px;
  font-size: 1.125rem;
  padding: 0 32px;
}

.loader-card {
  max-width: 520px;
  margin: 0 auto;
  padding: 24px 16px;
}
</style>
